import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import { Agent, RagFile } from '../lib/types';
import { processRagFile } from '../lib/rag';
import { encryptApiKey } from '../lib/api.ts';
import posthog from '../lib/posthog';
import { useRealtimeSync } from '../lib/hooks/useRealtimeSync';

interface AgentContextType {
  agents: Agent[];
  setAgents: React.Dispatch<React.SetStateAction<Agent[]>>;
  createAgent: (
    agent: Omit<
      Agent,
      'id' | 'created_at' | 'updated_at' | 'user_id' | 'is_example'
    >,
  ) => Promise<Agent>;
  updateAgent: (id: string, agent: Partial<Agent>) => Promise<Agent | null>;
  deleteAgent: (id: string) => Promise<void>;
  /** Reloads the list, e.g. after an edge function created an agent. */
  refreshAgents: () => Promise<void>;
  uploadRagFile: (agentId: string, file: File) => Promise<RagFile | null>;
  deleteRagFile: (fileId: string) => Promise<void>;
  getRagFiles: (agentId: string) => Promise<RagFile[]>;
  loading: boolean;
  error: string | null;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export function AgentProvider({ children }: { children: React.ReactNode }) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();
  const workspaceId = user?.currentWorkspace?.workspace_id;

  const fetchAgents = async () => {
    try {
      if (!user) {
        setAgents([]);
        setError(null);
        setLoading(false);
        return;
      }

      if (!workspaceId) {
        if (user.workspace_profiles == null) {
          setError(null);
          setLoading(true);
          return;
        }

        throw new Error('Workspace ID missing from user');
      }

      setLoading(true);
      const { data, error } = await supabase
        .from('agents')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setError(null);
      setAgents(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [user, workspaceId]);

  useRealtimeSync('agents', workspaceId, fetchAgents);

  const createAgent = async (
    agentData: Omit<
      Agent,
      'id' | 'created_at' | 'updated_at' | 'user_id' | 'is_example'
    >,
  ) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const normalizedName = agentData.name.trim().toLowerCase();

      // Check if agent name exists in this workspace
      const { data: existingAgentName, error: existingAgentNameError } =
        await supabase
          .from('agents')
          .select('id')
          .ilike('name', normalizedName)
          .eq('workspace_id', workspaceId)
          .limit(1);

      if (existingAgentNameError) throw existingAgentNameError;

      if (existingAgentName && existingAgentName.length > 0) {
        throw new Error('An agent with this name already exists.');
      }

      const { api_key, ...insertData } = agentData;
      const { data, error } = await supabase
        .from('agents')
        .insert([
          {
            ...insertData,
            is_example: false,
            user_id: user?.id,
            workspace_id: workspaceId,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      if (api_key) {
        await encryptApiKey({ agentId: data.id, apiKey: api_key, workspaceId });
      }

      posthog.capture('agent_created', {
        agent_id: data.id,
        workspace_id: workspaceId,
      });

      setAgents((current) => [data, ...current]);
      return data;
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to create agent',
      );
    }
  };

  const updateAgent = async (
    id: string,
    agentData: Partial<Agent>,
  ): Promise<Agent | null> => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { api_key, ...updateData } = agentData;
      const { data, error } = await supabase
        .from('agents')
        .update(updateData)
        .eq('id', id)
        .eq('workspace_id', workspaceId)
        .select()
        .single();

      if (error) throw error;

      if (api_key) {
        await encryptApiKey({ agentId: data.id, apiKey: api_key, workspaceId });
      }

      posthog.capture('agent_updated', {
        agent_id: id,
        workspace_id: workspaceId,
      });

      setAgents((current) =>
        current.map((agent) =>
          agent.id === id ? { ...agent, ...data } : agent,
        ),
      );
      return data;
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to update agent',
      );
    }
  };

  const deleteAgent = async (id: string) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { error } = await supabase
        .from('agents')
        .delete()
        .eq('id', id)
        .eq('workspace_id', workspaceId);

      if (error) throw error;

      posthog.capture('agent_deleted', {
        agent_id: id,
        workspace_id: workspaceId,
      });

      setAgents((current) => current.filter((agent) => agent.id !== id));
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to delete agent',
      );
    }
  };

  const uploadRagFile = async (
    agentId: string,
    file: File,
  ): Promise<RagFile | null> => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const content = await file.text();

      // Get agent configuration
      const { data: agent, error: agentError } = await supabase
        .from('agents')
        .select('chunk_size, chunk_overlap, embedding_model')
        .eq('id', agentId)
        .eq('workspace_id', workspaceId)
        .single();

      if (agentError) throw agentError;

      // Insert file record
      const { data, error } = await supabase
        .from('rag_files')
        .insert([
          {
            agent_id: agentId,
            name: file.name,
            content,
            user_id: user?.id,
            workspace_id: workspaceId,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      // Process file content and create embeddings
      await processRagFile(
        data.id,
        content,
        agent.chunk_size,
        agent.chunk_overlap,
        agent.embedding_model,
        workspaceId,
      );

      posthog.capture('rag_file_uploaded', {
        agent_id: agentId,
        workspace_id: workspaceId,
      });

      return data;
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to upload RAG file',
      );
    }
  };

  const deleteRagFile = async (fileId: string) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { error } = await supabase
        .from('rag_files')
        .delete()
        .eq('id', fileId)
        .eq('workspace_id', workspaceId);

      if (error) throw error;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : 'Failed to delete RAG file',
      );
    }
  };

  const getRagFiles = async (agentId: string): Promise<RagFile[]> => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { data, error } = await supabase
        .from('rag_files')
        .select('*')
        .eq('agent_id', agentId)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : 'Failed to fetch RAG files',
      );
    }
  };

  return (
    <AgentContext.Provider
      value={{
        agents,
        setAgents,
        createAgent,
        updateAgent,
        deleteAgent,
        refreshAgents: fetchAgents,
        uploadRagFile,
        deleteRagFile,
        getRagFiles,
        loading,
        error,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
}

export function useAgents() {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error('useAgents must be used within an AgentProvider');
  }
  return context;
}
